import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight, Menu, Phone, X } from "lucide-react";
import { scrollToSection } from "@/hooks/useLenis";
import { EASE } from "./Reveal";

const LINKS = [
    { label: "Manifesto", hash: "#manifesto", testid: "nav-link-manifesto" },
    { label: "Coverage", hash: "#plans", testid: "nav-link-coverage" },
    { label: "The Difference", hash: "#compare", testid: "nav-link-compare" },
    { label: "For Dealers", hash: "#dealers", testid: "nav-link-dealers" },
    { label: "FAQ", hash: "#faq", testid: "nav-link-faq" },
];

export const Navbar = ({ onQuote }) => {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 24);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const go = (hash) => {
        setOpen(false);
        scrollToSection(hash);
    };

    return (
        <motion.header
            initial={{ y: -80, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.9, ease: EASE }}
            data-testid="nav-container"
            className={`fixed inset-x-0 top-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-500 ${
                scrolled || open
                    ? "border-b border-white/10 bg-[#0A0D14]/85 backdrop-blur-xl"
                    : "border-b border-transparent bg-transparent"
            }`}
        >
            <div className="mx-auto flex h-[72px] max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
                <button
                    data-testid="nav-logo"
                    onClick={() => window.__lenis?.scrollTo(0, { duration: 1.4 })}
                    className="flex items-center gap-3"
                >
                    <img
                        src="/logo-horizontal.webp"
                        alt="AdvantaShield Warranty"
                        width="440"
                        height="146"
                        decoding="async"
                        className="h-12 w-auto drop-shadow-[0_0_18px_rgba(56,189,248,0.35)]"
                    />
                </button>

                <nav className="hidden items-center gap-9 lg:flex">
                    {LINKS.map((l) => (
                        <button
                            key={l.hash}
                            data-testid={l.testid}
                            onClick={() => go(l.hash)}
                            className="group relative text-sm font-medium text-slate-300 transition-colors duration-300 hover:text-white"
                        >
                            {l.label}
                            <span className="absolute -bottom-1.5 left-0 h-px w-0 bg-sky-400 transition-[width] duration-300 group-hover:w-full" />
                        </button>
                    ))}
                </nav>

                <div className="flex items-center gap-3">
                    <a
                        href="tel:+18886874889"
                        data-testid="nav-phone-link"
                        className="hidden items-center gap-2 text-sm font-semibold text-slate-300 transition-colors duration-300 hover:text-sky-300 xl:inline-flex"
                    >
                        <Phone className="h-4 w-4 text-sky-400" />
                        (888) 687-4889
                    </a>
                    <button
                        data-testid="nav-get-quote-btn"
                        onClick={onQuote}
                        className="group hidden items-center gap-2 rounded-full bg-sky-400 px-5 py-2.5 text-sm font-bold text-[#0A0D14] transition-[background-color,transform] duration-300 hover:bg-sky-300 hover:shadow-[0_0_32px_rgba(56,189,248,0.45)] active:scale-95 sm:inline-flex"
                    >
                        Get a Quote
                        <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                    </button>
                    <button
                        data-testid="nav-mobile-menu-btn"
                        onClick={() => setOpen((v) => !v)}
                        className="flex h-10 w-10 items-center justify-center rounded-lg border border-white/10 text-white lg:hidden"
                        aria-label="Toggle menu"
                    >
                        {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
                    </button>
                </div>
            </div>

            <AnimatePresence>
                {open && (
                    <motion.nav
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.35, ease: EASE }}
                        className="overflow-hidden border-t border-white/10 lg:hidden"
                        data-testid="nav-mobile-menu"
                    >
                        <div className="flex flex-col gap-1 px-4 py-4">
                            {LINKS.map((l) => (
                                <button
                                    key={l.hash}
                                    data-testid={`${l.testid}-mobile`}
                                    onClick={() => go(l.hash)}
                                    className="rounded-lg px-3 py-3 text-left text-base font-medium text-slate-200 transition-colors duration-200 hover:bg-white/5"
                                >
                                    {l.label}
                                </button>
                            ))}
                            <a
                                href="tel:+18886874889"
                                data-testid="nav-phone-link-mobile"
                                className="flex items-center gap-2.5 rounded-lg px-3 py-3 text-base font-semibold text-sky-300"
                            >
                                <Phone className="h-5 w-5" />
                                Call (888) 687-4889
                            </a>
                            <button
                                data-testid="nav-get-quote-btn-mobile"
                                onClick={() => {
                                    setOpen(false);
                                    onQuote();
                                }}
                                className="mt-2 rounded-full bg-sky-400 px-5 py-3 text-sm font-bold text-[#0A0D14]"
                            >
                                Get a Quote
                            </button>
                        </div>
                    </motion.nav>
                )}
            </AnimatePresence>
        </motion.header>
    );
};
